import type { JSX } from 'react';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileTextOutlined, LinkOutlined, PaperClipOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import { openingApi, type OpeningLifecycle } from '../lib/wikiApi';
import { partnerFileLink } from '../lib/room-files';

/**
 * Блоки B/C — партнёрские виджеты «Открытие точки» в статьях БЗ:
 *   • ProjectPlanWidget — приложить чертёж/план замеров (вместо Pyrus-формы),
 *     статья «Передать план в УК и проектировщику».
 *   • ProjectApprovalWidget — согласовать присланный УК проект / вернуть с
 *     замечаниями, статья «Утвердить проект».
 *
 * Оба используют общий Picker: подтягивают одобренные заявки осмотра партнёра
 * и дают выбрать помещение (если их несколько; одна — авто).
 */

const labelStyle: CSSProperties = { fontSize: 11.5, fontWeight: 700, color: 'var(--fk-text-2)', marginBottom: 5 };
const inputStyle: CSSProperties = {
  width: '100%', border: '1px solid var(--fk-border)', borderRadius: 'var(--fk-radius-md)',
  padding: '8px 11px', fontFamily: 'inherit', fontSize: 13.5, outline: 'none',
};
const card: CSSProperties = {
  marginTop: 8, border: '1px solid var(--fk-border)', borderRadius: 'var(--fk-radius-card)',
  padding: 18, background: 'var(--fk-surface)',
};

function isApproved(it: { status: string; stageCode: string | null }): boolean {
  return it.status === 'APPROVED' || it.status === 'CONVERTED' || it.stageCode === 'approved'
    || it.stageCode === 'plan_attached' || it.stageCode === 'project_review'
    || it.stageCode === 'project_approved' || it.stageCode === 'tech_plan_ready';
}

/** Обёртка-выбор помещения (одобренные заявки партнёра). */
function ApprovedPicker({ title, children }: {
  title: string;
  children: (id: string) => JSX.Element;
}): JSX.Element {
  const q = useQuery({
    queryKey: ['wiki', 'my-room-inspections'],
    queryFn: () => openingApi.myRoomInspections(),
    staleTime: 30_000,
    retry: false,
  });
  const approved = (q.data ?? []).filter(isApproved);
  const [picked, setPicked] = useState<string | null>(null);
  useEffect(() => {
    if (!picked && approved.length === 1) setPicked(approved[0].id);
  }, [approved, picked]);

  if (q.isLoading) return <div style={{ ...card, color: 'var(--fk-text-muted)', fontSize: 14 }}>Загружаем ваши заявки…</div>;

  if (approved.length === 0) {
    return (
      <div style={{ ...card, fontSize: 14, lineHeight: 1.6 }}>
        <div style={{ fontWeight: 800, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileTextOutlined style={{ color: 'var(--fk-info, #0096FF)' }} /> {title}
        </div>
        Доступно после того, как куратор <b>одобрит</b> вашу заявку на помещение.
        <div style={{ marginTop: 12 }}>
          <Link to="/opening/room-check" style={{ fontWeight: 700, color: 'var(--fk-info, #0096FF)', textDecoration: 'none' }}>
            Перейти к анализу помещения →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={card}>
      <div style={{ fontWeight: 800, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 15 }}>
        <FileTextOutlined style={{ color: 'var(--fk-info, #0096FF)' }} /> {title}
      </div>
      {/* Выбор помещения показываем ВСЕГДА (даже если заявка одна) — чтобы
          партнёр чётко видел, к КАКОЙ заявке цепляет план и «Уже приложено»
          относится именно к ней. Раньше при одной заявке выбор скрывался и
          было непонятно, к какой точке привязка. */}
      <div style={{ marginBottom: 14 }}>
        <div style={labelStyle}>
          {approved.length > 1 ? 'Выберите помещение' : 'Помещение'}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {approved.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => setPicked(it.id)}
              style={{
                textAlign: 'left', padding: '9px 12px', borderRadius: 'var(--fk-radius-md)', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 13.5,
                border: picked === it.id ? '1.5px solid var(--fk-info, #0096FF)' : '1px solid var(--fk-border)',
                background: picked === it.id ? 'rgba(0,150,255,.08)' : 'var(--fk-surface)',
                color: 'var(--fk-text)',
              }}>
              {it.address || 'Без адреса'}
            </button>
          ))}
        </div>
      </div>
      {picked && children(picked)}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--fk-border)' }}>
        <Link to="/opening/room-check" style={{ fontWeight: 700, fontSize: 13, color: 'var(--fk-info, #0096FF)', textDecoration: 'none' }}>
          Все мои заявки на помещение →
        </Link>
      </div>
    </div>
  );
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('Не удалось прочитать файл'));
    r.readAsDataURL(file);
  });
}

// ─────────────── Блок B: приложить чертёж ───────────────
// Экспортируется: переиспользуется в карточке заявки на странице
// «Мои заявки на помещение» (RoomReadinessPage), не только в статье БЗ.
export function PlanForm({ id }: { id: string }): JSX.Element {
  const qc = useQueryClient();
  const life = useQuery({
    queryKey: ['wiki', 'opening-lifecycle', id],
    queryFn: () => openingApi.openingLifecycle(id),
    retry: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Грузим выбранный файл сразу при выборе. Поле «ссылка» убрано — у партнёров
  // план из RemPlanner всегда файлом (.plan / PDF / изображение).
  const submit = async (file: File): Promise<void> => {
    setBusy(true); setErr(null); setOk(false);
    try {
      await openingApi.attachProjectPlan(id, {
        file: { name: file.name, dataUrl: await fileToDataUrl(file) },
      });
      setOk(true);
      if (fileRef.current) fileRef.current.value = '';
      await qc.invalidateQueries({ queryKey: ['wiki', 'opening-lifecycle', id] });
      await qc.invalidateQueries({ queryKey: ['wiki', 'my-room-inspections'] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось приложить план');
    } finally {
      setBusy(false);
    }
  };

  const plan = life.data?.projectPlan ?? [];
  return (
    <div>
      <div style={{ fontSize: 12.5, color: 'var(--fk-text-muted)', marginBottom: 12 }}>
        Приложите чертёж/план замеров из RemPlanner файлом (.plan, PDF или
        изображение). Он прикрепится к вашей заявке — куратор увидит план в CRM.
      </div>

      {plan.length > 0 && (
        <div style={{ marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={labelStyle}>Уже приложено</div>
          {plan.map((p, i) => {
            const link = partnerFileLink(p);
            const icon = p.kind === 'link' ? <LinkOutlined /> : <PaperClipOutlined />;
            // Файл ещё не выгружен на Google Диск — открывать нечем, пишем «загружается» (партнёру путь CRM не открыть).
            return link.href ? (
              <a key={i} href={link.href} target="_blank" rel="noreferrer"
                 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--fk-info, #0096FF)', textDecoration: 'none' }}>
                {icon} {p.name}
              </a>
            ) : (
              <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--fk-text-muted)' }}>
                {icon} {p.name}{link.pending ? ' · загружается' : ''}
              </span>
            );
          })}
        </div>
      )}

      <div>
        <div style={labelStyle}>Файл плана (.plan / PDF / изображение)</div>
        <input ref={fileRef} type="file" accept=".plan,.pdf,image/*" disabled={busy}
               onChange={(e) => { const f = e.target.files?.[0]; if (f) void submit(f); }}
               style={{ fontSize: 13 }} />
      </div>

      {busy && <div style={{ fontSize: 13, color: 'var(--fk-text-muted)', marginTop: 10, fontWeight: 600 }}>Загружаем…</div>}
      {err && <div style={{ fontSize: 13, color: 'var(--fk-error)', marginTop: 10, fontWeight: 600 }}>{err}</div>}
      {ok && <div style={{ fontSize: 13, color: 'var(--fk-success, #5C8A24)', marginTop: 10, fontWeight: 600 }}>План приложен ✓</div>}
    </div>
  );
}

export function ProjectPlanWidget(): JSX.Element {
  return <ApprovedPicker title="Передать чертёж/план в УК">{(id) => <PlanForm id={id} />}</ApprovedPicker>;
}

// ─────────────── Блок C: согласование проекта ───────────────
function ApprovalForm({ id }: { id: string }): JSX.Element {
  const qc = useQueryClient();
  const life = useQuery({
    queryKey: ['wiki', 'opening-lifecycle', id],
    queryFn: () => openingApi.openingLifecycle(id),
    retry: false,
  });
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const act = async (action: 'approve' | 'changes'): Promise<void> => {
    setBusy(true); setErr(null);
    try {
      if (action === 'changes' && !comment.trim()) { setErr('Опишите замечания'); setBusy(false); return; }
      await openingApi.reviewProject(id, { action, comment: comment.trim() || null });
      setComment('');
      await qc.invalidateQueries({ queryKey: ['wiki', 'opening-lifecycle', id] });
      await qc.invalidateQueries({ queryKey: ['wiki', 'my-room-inspections'] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось отправить решение');
    } finally {
      setBusy(false);
    }
  };

  const data: OpeningLifecycle | undefined = life.data;
  const project = data?.project ?? null;

  if (life.isLoading) return <div style={{ color: 'var(--fk-text-muted)', fontSize: 14 }}>Загружаем…</div>;

  if (!project || !project.fileUrl) {
    return (
      <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--fk-text)' }}>
        Проект ещё не загружен отделом проектирования. Когда УК пришлёт проект,
        здесь появятся кнопки согласования (придёт уведомление).
      </div>
    );
  }

  const statusLabel = project.status === 'approved' ? 'Согласован вами'
    : project.status === 'changes_requested' ? 'Возвращён с замечаниями' : 'На согласовании';
  // Ссылка на проект и финальный техплан: `driveUrl` после выгрузки на Google Диск, иначе публичный `url`.
  const projectLink = partnerFileLink({ url: project.fileUrl, driveUrl: project.driveUrl, storage: project.storage });
  const finalPlanLink = partnerFileLink({ url: project.finalPlanUrl, driveUrl: project.finalPlanDriveUrl, storage: project.storage });

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        {projectLink.href ? (
          <a href={projectLink.href} target="_blank" rel="noreferrer"
             style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: 'var(--fk-info, #0096FF)', textDecoration: 'none' }}>
            <FileTextOutlined /> {project.fileName || 'Проект помещения'}
          </a>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: 'var(--fk-text-muted)' }}>
            <FileTextOutlined /> {project.fileName || 'Проект помещения'}{projectLink.pending ? ' · загружается' : ''}
          </span>
        )}
        <div style={{ fontSize: 12.5, color: 'var(--fk-text-muted)', marginTop: 4 }}>Статус: {statusLabel}</div>
      </div>

      {project.status === 'approved' ? (
        <div style={{ fontSize: 13.5, color: 'var(--fk-success, #5C8A24)', fontWeight: 600 }}>
          Вы согласовали проект. УК готовит финальный технологический план.
          {project.finalPlanUrl && (
            <div style={{ marginTop: 8 }}>
              {finalPlanLink.href ? (
                <a href={finalPlanLink.href} target="_blank" rel="noreferrer" style={{ color: 'var(--fk-info, #0096FF)' }}>
                  Финальный техплан →
                </a>
              ) : (
                <span style={{ color: 'var(--fk-text-muted)' }}>
                  Финальный техплан{finalPlanLink.pending ? ' · загружается' : ''}
                </span>
              )}
            </div>
          )}
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 10 }}>
            <div style={labelStyle}>Замечания (если возвращаете проект)</div>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)}
                      placeholder="Например: нет возможности подвести воду к зоне…"
                      style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} />
          </div>
          {err && <div style={{ fontSize: 13, color: 'var(--fk-error)', marginBottom: 10, fontWeight: 600 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" disabled={busy} onClick={() => void act('approve')}
              style={{ padding: '10px 18px', borderRadius: 'var(--fk-radius-md)', border: 'none',
                       background: busy ? 'var(--fk-border-strong)' : 'var(--fk-success, #5C8A24)', color: '#fff',
                       fontSize: 14, fontWeight: 700, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit' }}>
              Согласовать
            </button>
            <button type="button" disabled={busy} onClick={() => void act('changes')}
              style={{ padding: '10px 18px', borderRadius: 'var(--fk-radius-md)',
                       border: '1px solid var(--fk-border-strong)', background: 'var(--fk-surface)',
                       color: 'var(--fk-text)', fontSize: 14, fontWeight: 700, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit' }}>
              Вернуть с замечаниями
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function ProjectApprovalWidget(): JSX.Element {
  return <ApprovedPicker title="Утвердить проект">{(id) => <ApprovalForm id={id} />}</ApprovedPicker>;
}
